/**
 * Base Application Error Class
 * All custom errors extend this class for consistent error handling
 * 
 * @module lib/errors/AppError
 */

export interface ErrorContext {
    [key: string]: unknown;
}

export interface AppErrorOptions {
    /** Error code for programmatic handling */
    code: string;
    /** HTTP status code (if applicable) */
    statusCode?: number;
    /** Additional context for debugging */
    context?: ErrorContext;
    /** Whether this is an operational error (expected) vs programmer error */
    isOperational?: boolean;
    /** Original error that caused this one */
    cause?: Error;
}

/**
 * Base error class for all application errors
 * 
 * @example
 * ```typescript
 * throw new AppError('Something went wrong', {
 *     code: 'UNKNOWN_ERROR',
 *     statusCode: 500,
 *     context: { userId: '123' }
 * });
 * ```
 */
export class AppError extends Error {
    /** Unique error code for this error type */
    public readonly code: string;

    /** HTTP status code (if applicable) */
    public readonly statusCode: number;

    /** Additional context for debugging */
    public readonly context: ErrorContext;

    /** Whether this is an operational error (expected) vs programmer error */
    public readonly isOperational: boolean;

    /** Timestamp when error occurred */
    public readonly timestamp: string;

    /** Original error that caused this one */
    public readonly cause?: Error;

    constructor(message: string, options: AppErrorOptions) {
        super(message);

        // Maintains proper stack trace for where error was thrown
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }

        this.name = this.constructor.name;
        this.code = options.code;
        this.statusCode = options.statusCode ?? 500;
        this.context = options.context ?? {};
        this.isOperational = options.isOperational ?? true;
        this.timestamp = new Date().toISOString();
        this.cause = options.cause;
    }

    /**
     * Convert error to a plain object for logging/serialization
     */
    toJSON(): Record<string, unknown> {
        return {
            name: this.name,
            message: this.message,
            code: this.code,
            statusCode: this.statusCode,
            context: this.context,
            isOperational: this.isOperational,
            timestamp: this.timestamp,
            stack: process.env.NODE_ENV === 'development' ? this.stack : undefined,
            cause: this.cause?.message,
        };
    }

    /**
     * Create a user-friendly message (hides technical details)
     */
    getUserMessage(): string {
        if (this.isOperational) {
            return this.message;
        }
        return 'An unexpected error occurred. Please try again later.';
    }

    /**
     * Create an AppError from an unknown error
     * Useful for wrapping errors in catch blocks
     */
    static from(error: unknown, options?: Partial<AppErrorOptions>): AppError {
        // Already an AppError
        if (error instanceof AppError) {
            return error;
        }

        // Standard Error
        if (error instanceof Error) {
            return new AppError(error.message, {
                code: options?.code ?? 'UNKNOWN_ERROR',
                statusCode: options?.statusCode ?? 500,
                context: options?.context ?? {},
                isOperational: options?.isOperational ?? false,
                cause: error,
            });
        }

        // Unknown error type
        return new AppError(String(error), {
            code: options?.code ?? 'UNKNOWN_ERROR',
            statusCode: options?.statusCode ?? 500,
            context: options?.context ?? {},
            isOperational: false,
        });
    }
}
